/* eslint-disable newline-before-return */
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { getAllFieldsFromSection } from "@/components/FormEngine/helpers/fieldGetters";
import {
  FieldImpact,
  FormField,
  FormFieldValue,
  ParsedForm,
} from "@/components/FormEngine/types";

import { StepRenderer } from "./components/StepRenderer";
import { StyledFormContainer } from "./styles";
import { FormState } from "./types";

/** Construit un FormState dérivé de parsedForm + fieldsMap (source de vérité). */
const buildEffectiveFormState = (
  parsedForm: ParsedForm,
  fieldsMap?: Record<string, any>,
): FormState => {
  const allFields: FormField[] = Array.from(parsedForm.steps.values()).flatMap(
    (step) =>
      Array.from(step.sections.values()).flatMap((section) =>
        getAllFieldsFromSection(section),
      ),
  );

  const state: FormState = {};
  for (const field of allFields) {
    const key = field.path.join(".");
    const ov = fieldsMap?.[key];

    if (ov && Object.prototype.hasOwnProperty.call(ov, "value")) {
      const ovVal = (ov as any).value;

      if (
        typeof field.type === "string" &&
        (field.type.toLowerCase() === "select" ||
          field.type.toLowerCase() === "radiobox")
      ) {
        const idStr = String(ovVal ?? "");
        const opt = field.options?.find((o) => o.id === idStr);
        state[field.id] = {
          id: idStr,
          value: idStr,
          text: opt?.value,
          impacts: undefined,
        };
      } else {
        state[field.id] = {
          id: field.value?.id ?? "",
          text: field.value?.text,
          impacts: field.value?.impacts,
          value:
            typeof ovVal === "string" ||
            typeof ovVal === "number" ||
            typeof ovVal === "boolean" ||
            ovVal === null
              ? ovVal
              : field.value?.value,
        };
      }
    } else {
      state[field.id] = field.value;
    }
  }
  return state;
};

export const FormRenderer: React.FC<{
  parsedForm: ParsedForm;
  fieldsMap?: Record<string, any>;
  onValueChange?: (
    fieldId: string,
    fieldPath: string[],
    next: FormFieldValue,
    impacts?: FieldImpact[],
  ) => void;
}> = ({ parsedForm, fieldsMap, onValueChange }) => {
  // uniquement l’étape active en local; les valeurs des champs sont dérivées
  const [activeStep, setActiveStep] = useState(0);

  // index id → field (pour récupérer rapidement path et options)
  const fieldById: Record<string, FormField> = useMemo(() => {
    const index: Record<string, FormField> = {};
    Array.from(parsedForm.steps.values()).forEach((step) =>
      Array.from(step.sections.values()).forEach((section) =>
        Array.from(section.subSections.values()).forEach((sub) =>
          sub.rows.forEach((row) =>
            row.fields.forEach((f) => {
              index[f.id] = f;
            }),
          ),
        ),
      ),
    );
    return index;
  }, [parsedForm]);

  // état effectif dérivé (pas de useState pour les valeurs)
  const formState: FormState = useMemo(
    () => buildEffectiveFormState(parsedForm, fieldsMap),
    [parsedForm, fieldsMap],
  );

  const handleFieldChange = (fieldId: string, next: FormFieldValue) => {
    // pas de setState local : la sélection est poussée en overrides par onValueChange
    // puis reflétée via fieldsMap → formState dérivé
    const f = fieldById[fieldId];
    if (f && onValueChange) onValueChange(fieldId, f.path, next, next?.impacts);
  };

  const steps = Array.from(parsedForm.steps.entries()).map(
    ([stepIndex, step]) => ({
      label: `Étape ${stepIndex}`,
      content: (
        <StepRenderer
          key={step.id}
          step={step}
          stepIndex={stepIndex}
          formState={formState}
          onFieldChange={handleFieldChange}
          fieldsMap={fieldsMap}
        />
      ),
    }),
  );

  const stepLabels = steps.map((s) => s.label);

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  return (
    <StyledFormContainer>
      {stepLabels.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
          <Button
            color='inherit'
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{
              minWidth: 100,
              opacity: activeStep === 0 ? 0.5 : 1,
              transition: "opacity 0.2s ease-in-out",
              "&:disabled": { color: "text.disabled" },
            }}
          >
            Précédent
          </Button>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ flex: 1 }}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Button
            onClick={handleNext}
            variant='primary'
            disabled={activeStep === stepLabels.length}
            sx={{
              minWidth: 100,
              opacity: activeStep === stepLabels.length ? 0.5 : 1,
              transition: "opacity 0.2s ease-in-out",
              "&:disabled": {
                backgroundColor: "action.disabledBackground",
                color: "text.disabled",
              },
            }}
          >
            {activeStep === stepLabels.length - 1 ? "Terminer" : "Suivant"}
          </Button>
        </Box>
      )}

      {stepLabels.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color='text.secondary'>
            Le formulaire généré s'affichera ici une fois que vous aurez cliqué
            sur "Générer le formulaire".
          </Typography>
        </Box>
      ) : activeStep === stepLabels.length ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant={"title1"} gutterBottom>
            Formulaire terminé
          </Typography>
          <Typography>Merci d'avoir rempli toutes les étapes.</Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 2, mb: 2, p: 2 }}>{steps[activeStep]?.content}</Box>
      )}
    </StyledFormContainer>
  );
};
